<template>
  <div class="customer-forms-tab">
    <PageLoader v-if="loading" message="Loading forms..." />

    <div
      v-else-if="loadFailed"
      class="no-results-message"
      data-cy="cf-load-error"
    >
      <i class="fad fa-triangle-exclamation"></i>
      Could not load this customer's forms.
      <button
        type="button"
        class="ml-2 text-blue-600 hover:underline"
        data-cy="cf-retry"
        @click="load"
      >
        Try again
      </button>
    </div>

    <!-- read-only, and no `#answer` slot: this aggregates across jobs, and a
         write belongs to the job that owns it — the Job page's Forms tab has the
         save layer, including the optimistic-locking and flush-on-leave handling
         that a second copy here would have to duplicate. -->
    <CustomFormSubmissionView
      v-else-if="currentSubmission"
      read-only
      :submission="currentSubmission"
      :focus-question-id="null"
      @back="clearSubmission"
    />

    <div v-show="!loading && !loadFailed && !currentSubmission">
      <DatatableReportV3
        v-if="pageData"
        id="customer-forms-table"
        :key="customerId"
        title="Forms &amp; Checklists"
        :source="pageData.source"
        :config="config"
        :filters="pageData.filters"
        subpage
        flat
        no-download
        :min-height="400"
        @row-click="selectRow"
      >
        <!-- The whole row is clickable; this only makes the form name look like
             the affordance it is. A click here bubbles to the row handler.

             v-html, not interpolation: the server's form cell is markup, not
             text — FormsAndChecklistsDatatable#form_field wraps an archived
             form's name in a strike-through span, which the All toggle makes
             reachable here. Interpolating would print the tag source. The name
             goes through content_tag server-side, so it is already escaped. Same
             reasoning as the Invoices and Quotes tabs beside this one. -->
        <!-- eslint-disable vue/no-v-html -->
        <template #column-form="row">
          <span
            class="text-blue-600 hover:underline"
            :data-cy="`customer-cf-row-${row.submission_id}`"
            v-html="row.columnValue"
          ></span>
        </template>
        <!-- eslint-enable vue/no-v-html -->
      </DatatableReportV3>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import PageLoader from "@/components/PageLoader.vue";
import {
  SUBMISSION_JOB_QUERY_KEY,
  SUBMISSION_QUERY_KEY
} from "@/CustomForms/customFormDeepLink";
import CustomFormSubmissionView from "@/CustomForms/CustomFormSubmissionView.vue";
import {
  submissionColumns,
  submissionStatusChipComponent
} from "@/CustomForms/formsAndChecklistsColumns";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import type { CustomFormSubmission } from "@/typings/customForm";

type SubmissionRow = {
  submission_id: number | string;
  submission_job_id: number | string;
  columnValue?: string;
};

const props = defineProps<{
  customerId: number | string;
}>();

const store = useStore();

const currentSubmission = ref<CustomFormSubmission | null>(null);
const loading = ref(true);
const loadFailed = ref(false);
// Source and filter definitions, fetched rather than passed as props: two of the
// filters build their options from tenant-wide queries, and this tab is lazily
// mounted, so the customer page must not prepay for them.
const pageData = ref<{ source: string; filters: unknown[] } | null>(null);

// Every path that closes the view bumps this, and a resolving fetch applies its
// result only while still the newest. Without it, a close that lands mid-fetch
// gets silently undone: browser Back, a tab switch, and a customer change can all
// null the open submission while a row's fetch is still in flight.
let selectionToken = 0;

// The Customer column is redundant here — every row is this customer's — and
// Department is dropped because no department filter is offered on this surface,
// which would leave the column with a generic search box in the header.
const config = {
  order: [[0, "desc"]],
  columns: submissionColumns({
    includeCustomer: false,
    includeDepartment: false
  }),
  vueComponents: [submissionStatusChipComponent]
};

// Nested under the job because that is where the detail endpoint lives, and only
// it returns the sections tree the view renders.
function fetchSubmission(
  jobId: number | string,
  id: number | string
): Promise<CustomFormSubmission> {
  return store.dispatch("fetchJobCustomFormSubmission", { jobId, id });
}

// Both ids go in the URL. The submission id alone cannot be restored — the detail
// endpoint is job-nested — so writing it by itself would leave a copied or
// reloaded link landing silently on the list.
async function open(jobId: number | string, id: number | string) {
  const token = ++selectionToken;
  loading.value = true;
  try {
    const full = await fetchSubmission(jobId, id);
    if (token !== selectionToken) return;

    currentSubmission.value = full;
  } finally {
    // Unconditional: a superseded fetch skipping this would strand the loader.
    loading.value = false;
  }
}

// Swallows rather than rethrows: this is invoked from DatatableV3's jQuery row
// handler, which has nowhere to put a rejected promise, so throwing would surface
// only as an unhandled rejection. The interceptor has already flashed the error;
// staying on the list is the right fallback.
async function selectRow(row: SubmissionRow) {
  try {
    await open(row.submission_job_id, row.submission_id);
  } catch {
    return;
  }
  if (!currentSubmission.value) return;

  updateQueryParam(SUBMISSION_JOB_QUERY_KEY, row.submission_job_id);
  updateQueryParam(SUBMISSION_QUERY_KEY, row.submission_id);
}

function clearSubmission() {
  selectionToken += 1;
  currentSubmission.value = null;
  updateQueryParam(SUBMISSION_QUERY_KEY, null);
  updateQueryParam(SUBMISSION_JOB_QUERY_KEY, null);
}

// Reads the URL rather than assuming a direction, so it serves a Back out of an
// open form and a Forward back into one. Bumps the token on the closing branch
// for the same reason clearSubmission does — but without writing the params,
// which mid-popstate would push a history entry on top of the one just restored.
async function syncFromQuery() {
  const query = getQueryVariables();
  const id = query[SUBMISSION_QUERY_KEY];
  const jobId = query[SUBMISSION_JOB_QUERY_KEY];

  if (!id || !jobId) {
    selectionToken += 1;
    currentSubmission.value = null;
    return;
  }
  if (String(currentSubmission.value?.id ?? "") === String(id)) return;

  await open(jobId, id);
}

// vue-router reuses this page across /customers/:id, so the tab is not remounted
// when the profile switches customers — CustomerShow watches the same prop to
// refetch the customer. An open form would stay on screen belonging to the
// previous one, and pageData.source would still name the previous customer's
// endpoint, so both are redone. The datatable is keyed on the id as well:
// DatatableV3 reads its source once at init, so a new source alone would not
// re-point it.
watch(
  () => props.customerId,
  (id, previous) => {
    if (id === previous) return;

    clearSubmission();
    load();
  }
);

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    pageData.value = await store.dispatch(
      "fetchCustomerCustomFormPageData",
      props.customerId
    );
  } catch {
    loadFailed.value = true;
    return;
  } finally {
    loading.value = false;
  }
  await syncFromQuery();
}

onMounted(() => {
  window.addEventListener("popstate", syncFromQuery);
  load();
});

onBeforeUnmount(() => window.removeEventListener("popstate", syncFromQuery));

// For CustomerShow's beforeTabChange, so returning to the tab starts at the list.
defineExpose({ setCurrentSubmission: clearSubmission });
</script>
